import { EuiFlexGroup, EuiLink } from "@inscopix/ideas-eui";
import { NotificationsFeedBodyQuery } from "graphql/_Types";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { NotificationAlert } from "./NotificationAlert";
import { useCallback, useState } from "react";
import { FileBadge } from "components/FileBadge/FileBadge";
import { RegionBadge } from "components/RegionBadge/RegionBadge";
import { isUndefined } from "lodash";
import assert from "assert";
import { isNonNull } from "utils/isNonNull";
import { ModalTaskLogsFileProcessing } from "./ModalTaskLogsFileProcessing";
import { useFlyoutContext } from "providers/FlyoutProvider/FlyoutProvider";
import { ExternalRegion } from "ideas.env";

export interface NotificationAlertFileProcessingProps {
  data: NonNullable<
    NotificationsFeedBodyQuery["notifications"]
  >["nodes"][number] & { region?: ExternalRegion };
  onNotificationRead: (
    notificationId: string,
    region?: ExternalRegion,
    refetchNotifications?: () => void,
  ) => void;
  refetchNotifications: () => void;
}

/** Component that renders a notification that a project was shared with the user */
export const NotificationAlertFileProcessing = ({
  data,
  onNotificationRead,
  refetchNotifications,
}: NotificationAlertFileProcessingProps) => {
  const { dateCreated, hasSeen, id: notificationId } = data;
  const attrs = data.fileProcessingNotification;
  assert(isNonNull(attrs));
  const { file } = attrs;
  assert(isNonNull(file));
  const project = file.project;
  assert(isNonNull(project));

  const routeMap = useRouteMapContext((s) => s.routeMap);
  const { closeFlyout } = useFlyoutContext();
  const [showModalTaskLogs, setShowModalTaskLogs] = useState(false);

  const onNotificationReadMemoized = useCallback(() => {
    if (!hasSeen) {
      onNotificationRead(notificationId, data.region, refetchNotifications);
    }
  }, [
    hasSeen,
    notificationId,
    onNotificationRead,
    refetchNotifications,
    data.region,
  ]);

  const actionProjectLink = (() => {
    if (project.tenant === null) {
      return null;
    }

    const tenantKey = project.tenant.key;
    const projectPath = routeMap["PROJECT"].dynamicPath({
      projectKey: project.key,
      tenantKey: tenantKey,
    }).path;

    const handleClick = (close = true) => {
      close && closeFlyout();
      onNotificationReadMemoized();
    };

    return isUndefined(data.region) ? (
      <EuiLink to={projectPath} onClick={() => handleClick()}>
        Go to project
      </EuiLink>
    ) : (
      <EuiLink
        href={`${data.region.URL}${projectPath}`}
        target="_blank"
        onClick={() => handleClick(false)}
      >
        Go to project <RegionBadge regionShortName={data.region.shortName} />
      </EuiLink>
    );
  })();

  return (
    <>
      {showModalTaskLogs && (
        <ModalTaskLogsFileProcessing
          fileId={file.id}
          onClose={() => setShowModalTaskLogs(false)}
        />
      )}
      <NotificationAlert
        dateCreated={dateCreated}
        hasSeen={hasSeen}
        title="Failed to process file"
        onNotificationRead={onNotificationReadMemoized}
        text={
          <span>
            An error occurred when reading metadata and generating previews for
            the file <FileBadge drsFile={file} /> in the project{" "}
            <strong>{project.name}</strong>
          </span>
        }
        action={
          <EuiFlexGroup>
            {actionProjectLink}
            {
              <EuiLink onClick={() => setShowModalTaskLogs(true)}>
                View log
              </EuiLink>
            }
          </EuiFlexGroup>
        }
      />
    </>
  );
};
